import React, { useState } from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, ListItem } from "@mui/material";
import { styled } from "@mui/system";
import Logo from "../../components/Icons/Logo";
import AccountMenuIcon from "../../components/Icons/AccountMenuIcon";
import LogoutMenuIcon from "../../components/Icons/LogoutMenuIcon";
import ClientMenuIcon from "../Icons/ClientMenuIcon";
import SupplierMenuIcon from "../Icons/SupplierMenuIcon";
import ProjectMenuIcon from "../Icons/ProjectMenuIcon";
import { makeStyles } from "../../styles";
import LogoWithoutO from "../Icons/LogoWithoutO";
import { NavLink, useLocation } from "react-router-dom";
import { logout } from "../../api/User";
import { COLORS } from "../../themes/Base";

// Placeholder icons – replace with your actual SVG imports
const LogoIcon = () => <Logo />;
const ProjectIcon = () => <ProjectMenuIcon />;
const SupplierIcon = () => <SupplierMenuIcon />;
const ClientIcon = () => <ClientMenuIcon />;
const AccountIcon = () => <AccountMenuIcon />;
const LogoutIcon = () => <LogoutMenuIcon />;

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: boolean }>(({ expanded }) => ({
  width: expanded ? "312px" : "76px",
  backgroundColor: COLORS.secondary,
  color: "white",
  height: "100vh",
  transition: "width 0.3s ease",
  display: "block",
  alignItems: "flex-start",
  position: "fixed",
  left: 0,
  top: 0,
  zIndex: 1200,
  overflow: "hidden",
}));

const useStyles = makeStyles()(() => ({
  logo: {
    display: "flex",
    justifyContent: "flex-start",
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "28px",
    paddingRight: "28px",
  },
  bottomMenuItems: {
    position: "absolute",
    bottom: "10px",
    width: "100%",
  },
  menuIcon: {
    width: "24px",
    height: "24px",
    display: "flex",
  },
}));

const Sidebar: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const { classes } = useStyles();

  const logUserOut = async () => {
    const logged_out = await logout();
    if (!logged_out) return;
    location.href = "/user/login";
  };

  const menuItemsTop = [
    { label: "Projects", icon: <ProjectIcon />, url: "/" },
    { label: "Suppliers", icon: <SupplierIcon />, url: "/suppliers" },
    // { label: "Clients", icon: <ClientIcon />, url: "" },
  ];

  const menuItemsBottom = [
    { label: "Profile", icon: <AccountIcon />, url: "/account" },
    { label: "Logout", icon: <LogoutIcon />, onClick: logUserOut },
  ];

  return (
    <SidebarContainer
      expanded={expanded}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div className={classes.logo}>
        <NavLink to="/" style={{ display: "flex" }}>
          <div className={classes.menuIcon} style={{ marginTop: "1px" }}>
            <LogoIcon />
          </div>
          <Box
            sx={{
              paddingLeft: "5px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: expanded ? 1 : 0,
              transform: expanded ? "translateX(0)" : "translateX(8px)",
              transition: "opacity 600ms ease, transform 400ms ease",
            }}
          >
            <LogoWithoutO />
          </Box>
        </NavLink>
      </div>
      <MenuItems items={menuItemsTop} expanded={expanded} />
      <div className={classes.bottomMenuItems}>
        <MenuItems items={menuItemsBottom} expanded={expanded} />
      </div>
    </SidebarContainer>
  );
};

type Props = {
  items: {
    label: string;
    icon: React.ReactNode;
    url?: string;
    onClick?: () => void;
  }[];
  expanded: boolean;
};

const MenuItems: React.FC<Props> = ({ items, expanded }) => {
  const { classes } = useStyles();
  const location = useLocation();
  return (
    <List sx={{ width: "100%", padding: 0 }}>
      {items.map((item, index) => (
        <ListItemButton
          key={index}
          component={Boolean(item.url) ? NavLink : "button"}
          to={Boolean(item.url) ? item.url : undefined}
          onClick={item.onClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            px: "28px",
            height: 46,
            mb: "4px",
            width: "100%",
            transition: "background 200ms, border-left 200ms",
            backgroundColor: location.pathname === item.url ? `${COLORS.bgHover}` : "none",
            borderLeft:
              location.pathname === item.url ? "3px solid white" : `3px solid ${COLORS.secondary}`,
            "&:hover": {
              backgroundColor: `${COLORS.bgHover}`,
              borderLeft: `3px solid ${COLORS.bgHover}`,
            },
            "&:focus": {
              backgroundColor: `${COLORS.bgHover}`,
              borderLeft: `3px solid ${COLORS.bgHover}`,
              border: `2px solid ${COLORS.borderFocus}`,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 2, color: "white" }}>
            <div className={classes.menuIcon}>{item.icon}</div>
          </ListItemIcon>
          <Box
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: expanded ? 1 : 0,
              transform: expanded ? "translateX(0)" : "translateX(8px)",
              transition: "opacity 600ms ease, transform 400ms ease",
            }}
          >
            <ListItemText primary={item.label} />
          </Box>
        </ListItemButton>
      ))}
    </List>
  );
};
export default Sidebar;
